import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/auth/auth.service';
import { CreateFeedbackIssueDto, FeedbackService } from 'src/app/services/feedback.service';

@Component({
  selector: 'app-report-issue',
  templateUrl: './report-issue.component.html',
  styleUrls: ['./report-issue.component.scss'],
})
export class ReportIssueComponent implements OnInit, OnChanges {
  @ViewChild('fileUpload') fileUpload: FileUpload;

  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Input() entityType?: string;
  @Input() entityId?: string | number;
  @Input() environment?: string;

  form: FormGroup;
  submitting: boolean = false;
  attachmentFiles: File[] = [];
  createdTicket: { key: string; url: string } | null = null;
  urlCopied: boolean = false;
  existingTickets: any[] = [];

  severityOptions = [
    { label: 'Low', value: 'Low' },
    { label: 'Medium', value: 'Medium' },
    { label: 'High', value: 'High' },
    { label: 'Critical', value: 'Critical' },
  ];

  sourceOptions = [
    'Content Issues',
    'Art',
    'Backend',
    'QA',
    'Other',
  ];

  issueTypeOptions = [
    { label: 'Bug', value: 'Bug' },
  ];


  get pageUrl(): string {
    return window.location.href;
  }

  get resolvedEnvironment(): string {
    return this.environment || (window as any).__env?.environment || '';
  }

  get reporterName(): string {
    return this.authService.currentUser?.name || '';
  }

  get reporterEmail(): string {
    return this.authService.currentUser?.email || '';
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly feedbackService: FeedbackService,
    private readonly messageService: MessageService,
    private readonly authService: AuthService,
    private readonly router: Router,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true && this.entityType && this.entityId != null) {
      this.feedbackService.getIssuesByEntity(this.entityType, this.entityId)
        .then((tickets) => { this.existingTickets = tickets; })
        .catch(() => { this.existingTickets = []; });
    }
    if (changes['visible']?.currentValue === false) {
      this.existingTickets = [];
    }
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      summary: ['', Validators.required],
      description: ['', Validators.required],
      stepsToReproduce: [''],
      expectedBehavior: [''],
      actualBehavior: [''],
      severity: ['Medium'],
      source: ['Content Issues', Validators.required],
      issueType: ['Bug', Validators.required],
    });
  }

  onFileSelect(event: any): void {
    const incoming: File[] = Array.from(event.files || []);
    this.attachmentFiles = [...this.attachmentFiles, ...incoming];
    // Reset the widget so it's ready for another selection
    setTimeout(() => this.fileUpload?.clear());
  }

  removeAttachment(index: number): void {
    this.attachmentFiles.splice(index, 1);
    if (this.attachmentFiles.length === 0) {
      this.fileUpload?.clear();
    }
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.form.reset({ severity: 'Medium', source: 'Content Issues', issueType: 'Bug' });
    this.attachmentFiles = [];
    this.createdTicket = null;
    this.urlCopied = false;
    this.fileUpload?.clear();
  }

  openTicket(url: string): void {
    window.open(url, '_blank');
  }

  viewInTable(source?: string): void {
    this.router.navigate(['/content-issues'], {
      queryParams: source ? { source } : {},
    });
  }

  async copyUrl(url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      this.urlCopied = true;
      setTimeout(() => (this.urlCopied = false), 2000);
    } catch {
      // Clipboard access denied — fall back to showing the URL
      this.urlCopied = false;
    }
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid || this.submitting) return;

    this.submitting = true;
    try {
      const dto: CreateFeedbackIssueDto = {
        ...this.form.value,
        entityType: this.entityType,
        entityId: this.entityId,
        environment: this.resolvedEnvironment,
        pageUrl: this.pageUrl,
      };

      const result = await this.feedbackService.createIssue(dto);

      if (this.attachmentFiles.length > 0) {
        const failed: string[] = [];
        for (const file of this.attachmentFiles) {
          try {
            await this.feedbackService.addAttachment(result.key, file);
          } catch {
            failed.push(file.name);
          }
        }
        if (failed.length > 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Attachment failed',
            detail: `${result.key} was created but ${failed.length === 1 ? 'one file' : `${failed.length} files`} could not be uploaded.`,
            life: 6000,
          });
        }
      }

      this.createdTicket = { key: result.key, url: result.url };
      this.close();
      this.messageService.add({ key: 'feedback-toast', severity: 'success', summary: result.key, data: { url: result.url, source: dto.source } });
    } catch (err) {
      this.messageService.add({
        severity: 'error',
        summary: 'Failed to create ticket',
        detail: 'Please try again or contact support.',
        life: 5000,
      });
    } finally {
      this.submitting = false;
    }
  }
}
